import { Component, OnInit, ViewChild } from '@angular/core';
import { RfpService } from 'src/app/services/rfp/rfp.service';
import { RfpSummaryDto } from 'src/app/domain/rfp.model';
import { Table } from 'primeng/table';
import { UIChart } from 'primeng/chart';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-rfp-reports',
  templateUrl: './rfp-dashboard.component.html',
  styleUrls: ['./rfp-dashboard.component.scss']
})
export class RfpDashboardComponent implements OnInit {
  @ViewChild('dtRfpSummary') table: Table;
  @ViewChild('rfpSummaryChart') rfpSummaryChart: UIChart;

  rfpSummary: RfpSummaryDto[] = [];
  filteredRfpSummary: RfpSummaryDto[] = [];
  rfpSummaryChartData: any;
  rfpSummaryChartOptions: any;

  selectedRfpSummaryDataType: string = 'rfpCount';
  selectedRfpSummaryGroupBy: string = 'customerName';

  rfpSummaryDataTypes: any[] = [
    { label: 'RFP Count', value: 'rfpCount' },
    { label: 'Total Project Duration', value: 'totalProjectDuration' },
    { label: 'Average Initial Bid Bond', value: 'averageInitialBidBond' },
    { label: 'Average Final Bid Bond', value: 'averageFinalBidBond' }
  ];

  rfpSummaryGroupByOptions: any[] = [
    { label: 'Customer Name', value: 'customerName' },
    { label: 'Rfp Type', value: 'deploymentType' }
  ];

  constructor(private rfpService: RfpService) {}

  ngOnInit() {
    this.loadRfpSummary();
    this.initializeRfpSummaryChart();
  }

  loadRfpSummary() {
    this.rfpService.getRfpSummary()
      .toPromise()
      .then(response => {
        this.rfpSummary = response;
        this.filteredRfpSummary = response;
        this.updateRfpSummaryChartData();
      })
      .catch(error => {
        console.error('Error loading RFP summary:', error);
      });
  }

  initializeRfpSummaryChart() {
    this.rfpSummaryChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          stacked: true,
        },
        y: {
          stacked: true,
          beginAtZero: true
        }
      },
      plugins: {
        tooltip: {
          callbacks: {
            title: (tooltipItems) => {
              return tooltipItems[0].label;
            },
            label: (tooltipItem) => {
              const datasetLabel = tooltipItem.dataset.label;
              const value = tooltipItem.raw;
              return `${datasetLabel}: ${value}`;
            },
            footer: (tooltipItems) => {
              // Calculate total for this specific x-axis label (column)
              const dataIndex = tooltipItems[0].dataIndex;
              const allDatasets = tooltipItems[0].chart.data.datasets;
              
              // Sum values from all datasets for this specific x-axis point
              let columnTotal = 0;
              for (let i = 0; i < allDatasets.length; i++) {
                if (allDatasets[i].data[dataIndex]) {
                  columnTotal += allDatasets[i].data[dataIndex];
                }
              }
              
              // Format based on data type
              if (this.selectedRfpSummaryDataType === 'averageInitialBidBond' || 
                  this.selectedRfpSummaryDataType === 'averageFinalBidBond') {
                return `Total: ${columnTotal.toFixed(2)}`;
              }
              
              return `Total: ${columnTotal}`;
            }
          }
        },
        legend: {
          position: 'right',
        }
      }
    };
  }

  updateRfpSummaryChartData() {
    let groupedData;
    if (this.selectedRfpSummaryGroupBy === 'customerName') {
      groupedData = this.groupDataByCustomer();
    } else {
      groupedData = this.groupDataByProductGroup();
    }

    const labels = Object.keys(groupedData);
    const datasets = this.createDatasets(groupedData);

    this.rfpSummaryChartData = {
      labels: labels,
      datasets: datasets
    };

    if (this.rfpSummaryChart) {
      this.rfpSummaryChart.reinit();
    }
  }

  groupDataByCustomer() {
    return this.filteredRfpSummary.reduce((acc, item) => {
      if (!acc[item.customerName]) {
        acc[item.customerName] = {};
      }
      acc[item.customerName][item.deploymentType] = item[this.selectedRfpSummaryDataType];
      return acc;
    }, {});
  }

  groupDataByProductGroup() {
    return this.filteredRfpSummary.reduce((acc, item) => {
      if (!acc[item.deploymentType]) {
        acc[item.deploymentType] = {};
      }
      acc[item.deploymentType][item.customerName] = item[this.selectedRfpSummaryDataType];
      return acc;
    }, {});
  }

  createDatasets(groupedData: { [key: string]: { [key: string]: number } }) {
    const groups = new Set<string>();
    Object.values(groupedData).forEach((data) => {
      Object.keys(data).forEach((group) => groups.add(group));
    });

    const groupArray = Array.from(groups);

    return groupArray.map((group) => {
      const data = Object.keys(groupedData).map((key) => groupedData[key][group] || 0);

      return {
        label: group,
        data: data,
      };
    });
  }

  onRfpSummaryDataTypeChange() {
    this.updateRfpSummaryChartData();
  }

  onRfpSummaryGroupByChange() {
    this.updateRfpSummaryChartData();
  }

  onTableFilter(event: any) {
    this.filteredRfpSummary = event.filteredValue || [];
    this.updateRfpSummaryChartData();
  }

  exportRfpSummaryPdf() {
    const doc = new jsPDF('landscape', 'pt', 'a4');

    const columns = [
      { header: '#', dataKey: 'index' },
      { header: 'Customer Name', dataKey: 'customerName' },
      { header: 'Deployment Type', dataKey: 'deploymentType' },
      { header: 'RFP Count', dataKey: 'rfpCount' },
      { header: 'Total Project Duration', dataKey: 'totalProjectDuration' },
      { header: 'Average Initial Bid Bond', dataKey: 'averageInitialBidBond' },
      { header: 'Average Final Bid Bond', dataKey: 'averageFinalBidBond' }
    ];

    const data = this.filteredRfpSummary;

    const rows = data.map((row: any, index: number) => ({
      index: index + 1,
      customerName: row.customerName,
      deploymentType: row.deploymentType,
      rfpCount: row.rfpCount,
      totalProjectDuration: row.totalProjectDuration,
      averageInitialBidBond: Number(row.averageInitialBidBond).toFixed(2),
      averageFinalBidBond: Number(row.averageFinalBidBond).toFixed(2)
    }));

    autoTable(doc, {
      columns: columns,
      body: rows,
      theme: 'grid',
      styles: {
        font: 'helvetica',
        fontSize: 10,
        cellPadding: 2,
        overflow: 'linebreak',
        cellWidth: 'wrap'
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontSize: 10,
        fontStyle: 'bold',
        halign: 'center'
      },
      alternateRowStyles: {
        fillColor: [240, 240, 240]
      },
      columnStyles: {
        index: { cellWidth: 26, halign: 'center' },
        customerName: { cellWidth: 67, halign: 'center' },
        deploymentType: { cellWidth: 49, halign: 'center' },
        rfpCount: { cellWidth: 54, halign: 'center' },
        totalProjectDuration: { cellWidth: 44, halign: 'center' },
        averageInitialBidBond: { cellWidth: 48.9, halign: 'center' },
        averageFinalBidBond: { cellWidth: 64, halign: 'center' }
      },
      margin: { top: 20 }
    });

    doc.save('RfpSummary.pdf');
  }

  exportRfpSummaryExcel() {
    import("xlsx").then(xlsx => {
      const worksheet = xlsx.utils.json_to_sheet(this.filteredRfpSummary.map(summary => ({
        'Customer Name': summary.customerName,
        'Deployment Type': summary.deploymentType,
        'RFP Count': summary.rfpCount,
        'Total Project Duration': summary.totalProjectDuration,
       'Average Initial Bid Bond': Number(summary.averageInitialBidBond).toFixed(2),
      'Average Final Bid Bond': Number(summary.averageFinalBidBond).toFixed(2)
      })));

      const workbook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
      const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
      this.saveAsExcelFile(excelBuffer, "RfpSummary");
    });
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], { type: EXCEL_TYPE });
    saveAs(data, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
  }

  exportRfpSummaryChartAsImage() {
    if (this.rfpSummaryChart && this.rfpSummaryChart.chart) {
      const chart = this.rfpSummaryChart.chart;
      const canvas = chart.canvas;
      const ctx = canvas.getContext('2d');

      ctx.save();
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const base64Image = canvas.toDataURL('image/png');
      ctx.restore();

      const link = document.createElement('a');
      link.href = base64Image;
      link.download = 'rfp-summary-chart.png';
      link.click();
    }
  }

  exportRfpSummaryChartAsPDF() {
    if (this.rfpSummaryChart && this.rfpSummaryChart.chart) {
      const chart = this.rfpSummaryChart.chart;
      const canvas = chart.canvas;
      const ctx = canvas.getContext('2d');

      ctx.save();
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const base64Image = canvas.toDataURL('image/png');
      ctx.restore();

      const imgWidth = 280;
      const imgHeight = (canvas.height / canvas.width) * imgWidth;

      const pdf = new jsPDF('landscape');
      pdf.addImage(base64Image, 'PNG', 10, 10, imgWidth, imgHeight);
      pdf.save('rfp-summary-chart.pdf');
    }
  }
}